import { useState, useEffect } from 'react';
import { toggleVenueArchived, combineVenues } from '../../../util/util';
import DropdownPicker from '../ui/DropdownPicker';
import Modal from './Modal';
import ModalActions from './ModalActions';
import { useToast } from '../../ui/Toast';

function VenueArchiveModal({
  isModalOpen,
  setIsModalOpen,
  selectedItem,
  combineTargets,
  onVenueArchived,
  onVenuesCombined,
}) {
  const { showSuccess, showError } = useToast();
  const [showCombinePicker, setShowCombinePicker] = useState(false);
  const [combineTarget, setCombineTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isArchived = !!selectedItem?.is_archived;

  useEffect(() => {
    setShowCombinePicker(false);
    setCombineTarget(null);
    setIsLoading(false);
  }, [isModalOpen]);

  const handleArchive = async () => {
    if (!selectedItem) return;
    setIsLoading(true);
    try {
      await toggleVenueArchived(selectedItem.venue_id, isArchived);
      onVenueArchived?.(selectedItem.venue_id, !isArchived);
      setIsModalOpen(false);
      showSuccess(isArchived ? 'Venue restored.' : 'Venue archived.');
    } catch (error) {
      console.error('Error archiving venue:', error);
      showError(`Failed to archive venue: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCombine = async () => {
    if (!combineTarget || !selectedItem) return;
    setIsLoading(true);
    const targetVenue = combineTargets.find(v => String(v.venue_id) === combineTarget);
    try {
      await combineVenues(targetVenue.venue_id, selectedItem.venue_id);
      onVenuesCombined?.(targetVenue.venue_id, selectedItem.venue_id);
      setIsModalOpen(false);
      showSuccess('Venues combined.');
    } catch (error) {
      console.error('Error combining venues:', error);
      showError(`Failed to combine venues: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => !isLoading && setIsModalOpen(false)}
      title={
        showCombinePicker
          ? 'Combine into which venue?'
          : `${isArchived ? 'Restore' : 'Archive'} "${selectedItem?.venue_name}"?`
      }
      maxWidth="max-w-md"
      clipContent={false}
    >
      {!showCombinePicker ? (
        <>
          <p className="text-sm text-slate-400">
            {isArchived
              ? 'This venue will become visible again in the feed and search.'
              : 'This venue will be hidden from the feed and search, but will still count in your analytics. Its shows will remain visible in the feed and search unless you archive them individually. You can restore it anytime from the "Display Archived" button in the Manage tab.'}
          </p>
          <ModalActions
            onCancel={() => setIsModalOpen(false)}
            onConfirm={handleArchive}
            confirmLabel={isArchived ? 'Restore' : 'Archive'}
            confirmClassName={isArchived ? 'btn-primary w-28' : 'btn-error btn-outline w-28'}
            isLoading={isLoading}
          >
            {combineTargets?.length > 0 && !isArchived && (
              <button
                type="button"
                className="btn btn-outline btn-sm mr-auto focus:outline-none"
                disabled={isLoading}
                onClick={() => setShowCombinePicker(true)}
              >
                Combine
              </button>
            )}
          </ModalActions>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-400 mb-4">
            All shows from "{selectedItem?.venue_name}" will be moved to the selected
            venue, and this venue will be archived.
          </p>
          <div>
            <DropdownPicker
              options={combineTargets.map(v => ({
                key: String(v.venue_id),
                label: v.venue_name,
                sublabel: [v.address1, v.neighborhood].filter(Boolean).join(' · ') || undefined,
              }))}
              value={combineTarget}
              onChange={setCombineTarget}
              placeholder="Select a venue…"
              width="w-full"
              variant="form"
            />
          </div>
          <ModalActions
            cancelLabel="Back"
            onCancel={() => {
              setShowCombinePicker(false);
              setCombineTarget(null);
            }}
            onConfirm={handleCombine}
            confirmLabel="Combine"
            confirmClassName="btn-primary w-28"
            isLoading={isLoading}
            confirmDisabled={!combineTarget}
          />
        </>
      )}
    </Modal>
  );
}

export default VenueArchiveModal;
